import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Box, Button, Stack, useMediaQuery, CircularProgress } from '@mui/material'
import { CommentsTabsStack, SideBox, MainStackStyle, CommentsButton } from './style'
import { PureLightTheme } from '../../../styles/theme'
import { NotFound } from '../../../components/ui-kit/NotFound'
import { ProjectAccessRestrictedStub } from '../../../components/ProjectPage/ProjectAccessRestrictedStub'
import { ProjectTabs } from '../../../components/ProjectPage/ProjectTabs'
import { CommentsWrapper } from '../../../components/ProjectPage/CommentsWrapper'
import { ProjectInfo } from '../../../components/ProjectPage/ProjectInfo'
import { IconComments } from '../../../icons/IconComments'
import { CommentsDrawer } from '../../../components/ui-kit/CommentsDrawer'
import { projectsApi, useGetProjectQuery, useTrackProjectViewMutation } from '../../../api'
import { useAppDispatch } from '../../../hooks'
import { commentsButtonPwaSx } from '../../../consts/mobileLayout'
import { usePwaMobile } from '../../../hooks/usePwaMobile'
import {
  COMMENT_ID_QUERY_KEY,
  COMMENT_TAB_QUERY_KEY,
  DISCUSSION_QUERY_KEY,
  DISCUSSION_QUERY_VALUE,
  NOTIF_SCROLL_SEQ_QUERY_KEY,
  PARENT_COMMENT_ID_QUERY_KEY,
  SCROLL_QUERY_KEY,
  SCROLL_QUERY_VALUE,
} from '../../../consts/commentNotificationQueryKeys'
import {
  parseCommentTabParam,
  parsePositiveIntParam,
  stripNotificationCommentParams,
} from '../../../utils/commentNotificationLink'
import { formatNumber } from '../../../common/FormatNumber'
import { resolveProjectCommentsTotal } from '../../../api/types'
import { extractApiErrorMessage, isProjectAccessRestrictedError } from '../../../utils/api'

export const ProjectPage = () => {
  useLayoutEffect(() => {
    document.title = 'Проект'
  }, [])

  const isTablet = useMediaQuery(PureLightTheme.breakpoints.down('md'))
  const isPwaMobile = usePwaMobile()
  const { slug } = useParams<{ slug: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const dispatch = useAppDispatch()
  const {
    data: projectData,
    error: projectError,
    isLoading,
    isFetching,
    isError,
  } = useGetProjectQuery(slug ?? '', {
    skip: !slug,
    refetchOnMountOrArgChange: true,
  })
  const [fetchSettled, setFetchSettled] = useState(false)
  const [trackProjectView] = useTrackProjectViewMutation()

  const isAccessRestricted = isProjectAccessRestrictedError(projectError)

  useEffect(() => {
    setFetchSettled(false)
  }, [slug])

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setFetchSettled(true)
    }
  }, [isLoading, isFetching, slug])

  const [openComments, setOpenComments] = useState(false)
  const viewTrackedRef = useRef(false)
  const discussionFromQueryAppliedRef = useRef(false)
  const desktopCommentsScrollFromQueryAppliedRef = useRef(false)
  const commentsSectionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    discussionFromQueryAppliedRef.current = false
    desktopCommentsScrollFromQueryAppliedRef.current = false
    viewTrackedRef.current = false
  }, [slug])

  useEffect(() => {
    if (isLoading || !projectData?.id || !slug) {
      return
    }
    if (viewTrackedRef.current) {
      return
    }
    viewTrackedRef.current = true

    trackProjectView({ projectId: projectData.id })
      .unwrap()
      .then(({ views }) => {
        dispatch(
          projectsApi.util.updateQueryData('getProject', slug, (draft) => {
            if (draft?.counters) {
              draft.counters.views = views
            }
          })
        )
      })
      .catch(() => {
        viewTrackedRef.current = false
      })
  }, [dispatch, isLoading, projectData?.id, slug, trackProjectView])

  useEffect(() => {
    if (isLoading || !projectData) return
    if (!isTablet) return
    if (searchParams.get(DISCUSSION_QUERY_KEY) !== DISCUSSION_QUERY_VALUE) {
      discussionFromQueryAppliedRef.current = false
      return
    }
    if (discussionFromQueryAppliedRef.current) return
    discussionFromQueryAppliedRef.current = true
    setOpenComments(true)
  }, [isLoading, projectData, isTablet, searchParams])

  const notificationCommentIdFromQuery = parsePositiveIntParam(
    searchParams.get(COMMENT_ID_QUERY_KEY),
  )

  useEffect(() => {
    if (isLoading || !projectData) return
    if (isTablet) return
    if (searchParams.get(SCROLL_QUERY_KEY) !== SCROLL_QUERY_VALUE) return

    const isNotificationScroll = notificationCommentIdFromQuery != null

    if (!isNotificationScroll) {
      if (desktopCommentsScrollFromQueryAppliedRef.current) return
      desktopCommentsScrollFromQueryAppliedRef.current = true
    }

    const stripScrollOnly = !isNotificationScroll

    let cancelled = false
    let stripDone = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const supportsScrollEnd = typeof window !== 'undefined' && 'onscrollend' in window
    let scrollEndHandler: EventListener | undefined

    const stripQuery = () => {
      if (!stripScrollOnly || stripDone || cancelled) return
      stripDone = true
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
        timeoutId = undefined
      }
      if (scrollEndHandler !== undefined) {
        window.removeEventListener('scrollend', scrollEndHandler)
        scrollEndHandler = undefined
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(SCROLL_QUERY_KEY)
          return next
        },
        { replace: true },
      )
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return
        commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

        if (stripScrollOnly) {
          if (supportsScrollEnd) {
            scrollEndHandler = () => {
              stripQuery()
            }
            window.addEventListener('scrollend', scrollEndHandler, { once: true })
          }
          timeoutId = setTimeout(stripQuery, 800)
        }
      })
    })

    return () => {
      cancelled = true
      if (timeoutId !== undefined) clearTimeout(timeoutId)
      if (scrollEndHandler !== undefined) {
        window.removeEventListener('scrollend', scrollEndHandler)
      }
      if (!stripDone) {
        desktopCommentsScrollFromQueryAppliedRef.current = false
      }
    }
  }, [
    isLoading,
    projectData,
    isTablet,
    searchParams,
    setSearchParams,
    notificationCommentIdFromQuery,
  ])

  const handleOpenComments = () => setOpenComments(true)

  const notificationLinkProps = useMemo(
    () => ({
      initialCommentTab: parseCommentTabParam(searchParams.get(COMMENT_TAB_QUERY_KEY)),
      notificationParentCommentId: parsePositiveIntParam(
        searchParams.get(PARENT_COMMENT_ID_QUERY_KEY),
      ),
      notificationCommentId: parsePositiveIntParam(searchParams.get(COMMENT_ID_QUERY_KEY)),
    }),
    [searchParams],
  )

  const handleNotificationDeepLinkConsumed = useCallback(() => {
    discussionFromQueryAppliedRef.current = false
    setSearchParams((prev) => stripNotificationCommentParams(prev), { replace: true })
  }, [setSearchParams])

  const handleCloseComments = () => {
    setOpenComments(false)
    discussionFromQueryAppliedRef.current = false
    const hasDrawerQuery =
      searchParams.has(DISCUSSION_QUERY_KEY) ||
      searchParams.has(COMMENT_TAB_QUERY_KEY) ||
      searchParams.has(PARENT_COMMENT_ID_QUERY_KEY) ||
      searchParams.has(COMMENT_ID_QUERY_KEY) ||
      searchParams.has(SCROLL_QUERY_KEY) ||
      searchParams.has(NOTIF_SCROLL_SEQ_QUERY_KEY)
    if (!hasDrawerQuery) return
    const next = stripNotificationCommentParams(searchParams)
    const search = next.toString()
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    )
  }

  if (!fetchSettled || isLoading || isFetching) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (isAccessRestricted) {
    return <ProjectAccessRestrictedStub message={extractApiErrorMessage(projectError)} />
  }

  if (isError || !projectData) return <NotFound text={'Проект не найден'} />

  const isTeamSpaceTab = location.pathname.includes('/team-space')

  return (
    <>
      <Stack direction='row' sx={MainStackStyle}>
        <Stack direction="column" sx={CommentsTabsStack}>
          <ProjectTabs project={projectData} />
          {!isTablet && !isTeamSpaceTab && (
            <Box
              ref={commentsSectionRef}
              sx={{
                width: '100%',
                minWidth: 0,
                scrollMarginTop: 80,
              }}
            >
              <CommentsWrapper
                {...notificationLinkProps}
                commentsSectionRootRef={commentsSectionRef}
                onNotificationDeepLinkConsumed={handleNotificationDeepLinkConsumed}
              />
            </Box>
          )}
          {isTablet && !isTeamSpaceTab && (
            <Button 
              variant="action"
              disableRipple
              startIcon={<IconComments fontSize='small' />}
              onClick={handleOpenComments}
              sx={[CommentsButton, isPwaMobile && commentsButtonPwaSx]}
            >
              Дискуссия {formatNumber(resolveProjectCommentsTotal(projectData.counters.comments))}
            </Button>
          )}
        </Stack>
        {!isTablet && (
          <Box sx={SideBox}>
            <ProjectInfo project={projectData} />
          </Box>
        )}
      </Stack>
      {isTablet && !isTeamSpaceTab && (
        <CommentsDrawer open={openComments} onClose={handleCloseComments}>
          <CommentsWrapper
            onClose={handleCloseComments}
            {...notificationLinkProps}
            onNotificationDeepLinkConsumed={handleNotificationDeepLinkConsumed}
          />
        </CommentsDrawer>
      )}
    </>
  )
}
